/**
 * Read access to structure revisions (mig 730). Read-only by design: revisions
 * are written by the database — the baseline, a minor revision on every edit
 * to a live tree, and (Phase 3) an authorized reorg. No client ever writes one.
 */

import { getTypedSupabaseClient } from '../typedSupabaseClient';
import type { Database } from '../../types/database.generated';
import type { HierarchyKind } from './hierarchyTypes';
import type {
  StructureRevision,
  StructureRevisionNode,
  StructureRevisionOrigin,
  StructureRevisionStatus,
} from './structureRevisions';

type RevisionRow = Database['public']['Tables']['structure_revisions']['Row'];
type RevisionNodeRow = Database['public']['Tables']['structure_revision_nodes']['Row'];

function toRevision(row: RevisionRow): StructureRevision {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    kind: row.hierarchy_kind as HierarchyKind,
    major: row.major,
    minor: row.minor,
    origin: row.origin as StructureRevisionOrigin,
    status: row.status as StructureRevisionStatus,
    label: row.label,
    rationale: row.rationale,
    nodeCount: row.node_count,
    effectiveAt: row.effective_at,
    authorizedBy: row.authorized_by,
    supersedesRevisionId: row.supersedes_revision_id,
  };
}

function toRevisionNode(row: RevisionNodeRow): StructureRevisionNode {
  return {
    revisionId: row.revision_id,
    nodeId: row.node_id,
    parentNodeId: row.parent_node_id,
    name: row.name,
    level: row.level,
    depth: row.depth,
    sortOrder: row.sort_order,
    pathNames: row.path_names ?? [],
  };
}

/** Every revision of one tree, newest first. */
export async function listStructureRevisions(workspaceId: string, kind: HierarchyKind): Promise<StructureRevision[]> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('structure_revisions')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('hierarchy_kind', kind)
    // An admin can read their own open draft (mig 732); history is what was authorized.
    .neq('status', 'draft')
    .order('major', { ascending: false })
    .order('minor', { ascending: false });
  if (error) throw new Error(`Failed to load structure history: ${error.message}`);
  return (data ?? []).map(toRevision);
}

/** The frozen tree of one revision. */
export async function listStructureRevisionNodes(revisionId: string): Promise<StructureRevisionNode[]> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('structure_revision_nodes')
    .select('*')
    .eq('revision_id', revisionId);
  if (error) throw new Error(`Failed to load structure version: ${error.message}`);
  return (data ?? []).map(toRevisionNode);
}
